import moment from 'moment-timezone';
import { Service } from 'resources/types';

interface CalendarEventData {
    date: string;
    time: string;
    barberName: string;
    services: Service[];
}

/**
 * Generates iCal file content for a calendar event
 * @param data - The reservation data including date, time, barber name, and services
 * @returns iCal formatted string
 */
export const generateCalendarEvent = (data: CalendarEventData): string => {
    const { date, time, barberName, services } = data;

    // Parse date and time
    const [hours, minutes] = time.split(':').map(Number);
    // Parse date in the same way as generateFormattedDate
    const eventDate = moment(date).tz('Europe/Belgrade');
    eventDate.hour(hours);
    eventDate.minute(minutes);
    eventDate.second(0);

    // Calculate duration based on services (duration is in minutes)
    const totalDuration = services.reduce((sum, service) => sum + (service.duration || 60), 0);
    const endDate = moment(eventDate).add(totalDuration, 'minutes');

    // Format dates for iCal with timezone (YYYYMMDDTHHmmss)
    const formatICalDate = (date: moment.Moment): string => {
        return date.format('YYYYMMDDTHHmmss');
    };

    const dtStart = formatICalDate(eventDate);
    const dtEnd = formatICalDate(endDate);
    const dtStamp = formatICalDate(moment());

    // Generate unique ID for the event
    const uid = `${date}-${time.replace(':', '')}-${barberName.replace(
        /\s+/g,
        '-',
    )}@trend.grooming`;

    // Generate description with services - using simple format for better compatibility
    const servicesList = services.map(service => `${service.title}`).join(', ');
    const description = `Services: ${servicesList} | Phone: +383 44 600 126 | Email: trend.grooming@gmail.com`;

    // Escape special characters for iCal format
    const escapeICalText = (text: string): string => {
        return text.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,');
    };

    // Generate iCal content
    const icsContent = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//Trend Grooming//Reservation System//EN',
        'CALSCALE:GREGORIAN',
        'METHOD:PUBLISH',
        'BEGIN:VTIMEZONE',
        'TZID:Europe/Belgrade',
        'BEGIN:STANDARD',
        'DTSTART:19701025T030000',
        'RRULE:FREQ=YEARLY;BYDAY=-1SU;BYMONTH=10',
        'TZOFFSETFROM:+0200',
        'TZOFFSETTO:+0100',
        'END:STANDARD',
        'BEGIN:DAYLIGHT',
        'DTSTART:19700329T020000',
        'RRULE:FREQ=YEARLY;BYDAY=-1SU;BYMONTH=3',
        'TZOFFSETFROM:+0100',
        'TZOFFSETTO:+0200',
        'END:DAYLIGHT',
        'END:VTIMEZONE',
        'BEGIN:VEVENT',
        `UID:${uid}`,
        `DTSTAMP:${dtStamp}`,
        `DTSTART;TZID=Europe/Belgrade:${dtStart}`,
        `DTEND;TZID=Europe/Belgrade:${dtEnd}`,
        `SUMMARY:${escapeICalText(`Termin me ${barberName} | Trend Grooming`)}`,
        `DESCRIPTION:${escapeICalText(description)}`,
        `LOCATION:${escapeICalText('Rruga Robert Doll, 118, Prishtinë')}`,
        `ORGANIZER;CN=Trend Grooming:mailto:trend.grooming@gmail.com`,
        'STATUS:CONFIRMED',
        'SEQUENCE:0',
        'BEGIN:VALARM',
        'TRIGGER:-PT1H',
        'ACTION:DISPLAY',
        `DESCRIPTION:${escapeICalText(`Kujtesë: Termini me ${barberName} në 1 orë`)}`,
        'END:VALARM',
        'END:VEVENT',
        'END:VCALENDAR',
    ].join('\r\n');

    return icsContent;
};
